import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as assistantsService from '@/services/assistants.service';
import type { CreateAssistantRequest, UpdateAssistantRequest } from '@/types/api.types';

export const useAssistants = () => {
  return useQuery({
    queryKey: ['assistants-admin'],
    queryFn: () => assistantsService.listAssistants(),
    staleTime: 10 * 1000,
  });
};

export const useAssistant = (assistantId: string | null) => {
  return useQuery({
    queryKey: ['assistants-admin', assistantId],
    queryFn: () => assistantsService.getAssistant(assistantId!),
    enabled: !!assistantId,
    staleTime: 10 * 1000,
  });
};

export const useEnabledAssistants = () => {
  return useQuery({
    queryKey: ['assistants-enabled'],
    queryFn: () => assistantsService.listEnabledAssistants(),
    staleTime: 60 * 1000,
  });
};

export const useCreateAssistant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssistantRequest) => assistantsService.createAssistant(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants-admin'] });
      queryClient.invalidateQueries({ queryKey: ['assistants-enabled'] });
    },
  });
};

export const useUpdateAssistant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assistantId, data }: { assistantId: string; data: UpdateAssistantRequest }) =>
      assistantsService.updateAssistant(assistantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants-admin'] });
      queryClient.invalidateQueries({ queryKey: ['assistants-enabled'] });
    },
  });
};

export const useDeleteAssistant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assistantId: string) => assistantsService.deleteAssistant(assistantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants-admin'] });
      queryClient.invalidateQueries({ queryKey: ['assistants-enabled'] });
    },
  });
};

export const useTestAssistant = () => {
  return useMutation({
    mutationFn: ({ assistantId, prompt }: { assistantId: string; prompt: string }) =>
      assistantsService.testAssistant(assistantId, prompt),
  });
};

export const useAISettings = () => {
  return useQuery({
    queryKey: ['ai-settings'],
    queryFn: () => assistantsService.getAISettings(),
    staleTime: 30 * 1000,
  });
};

export const useUpdateAISettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) => assistantsService.updateAISettings(apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-settings'] });
    },
  });
};

export const useTestAIConnection = () => {
  return useMutation({
    mutationFn: () => assistantsService.testAIConnection(),
  });
};
